import type { LensColumnKind } from '~/types/leLens';

import { type RatioFault, isPerUnitColumn, ratioParts, ratioPartsForAsk } from './ratioGuard';

/**
 * Which of the three ways to answer a request is being used.
 *
 * Cheapest last, which is the order the decision is made in: a view change
 * costs nothing, a calculated field costs nothing to run and is correct at
 * every grain, and a warehouse query costs bytes, a wait, and one more thing
 * on the board that can disagree with its neighbours.
 */
export const LENS_TIERS = ['query', 'field', 'view'] as const;

export type LensTier = (typeof LENS_TIERS)[number];

/** 1 is a warehouse query, 3 is a view change. Higher is cheaper. */
export const TIER_RANK: Record<LensTier, number> = { query: 1, field: 2, view: 3 };

export type TierDataset = {
  id: string;
  name?: string;
  columns: ReadonlyArray<string>;
  /**
   * A column's declared kind, keyed by name, from a BigQuery dry run (00_PLAN.md's kind
   * inference) or an author's override. Read ahead of the CONCEPTS regex below for any column it
   * covers, so a numeric id a dry run declared a dimension is never counted as a measure just
   * because its name matches a business word, and a column an author declared a measure is
   * counted as one even where its name reads as a label. A dataset that has not been dry-run yet
   * carries no kinds at all, and a column absent from the map falls back to the regex on its own.
   */
  kinds?: Readonly<Record<string, LensColumnKind>>;
  /** The query text or source table the dataset reads. */
  reads?: string;
};

/** The arithmetic a request is asking for, beyond totalling a column. */
export type TierOperation = 'none' | 'ratio' | 'share' | 'average' | 'perUnit' | 'difference' | 'conditionalSubtotal';

export type TierEvidence = {
  /** The dataset that resolved the most of the request, when one did. */
  datasetId?: string;
  /** How that dataset is named to a reader; the id may be a row identifier nobody can read. */
  datasetName?: string;
  /** Columns the request names that a dataset already returns. */
  matched: Array<string>;
  /** Of those, the ones that measure a row rather than label it. */
  measures: Array<string>;
  /** Things the request names that no dataset carries. Each forces a query. */
  missing: Array<string>;
  /** The arithmetic detected in the wording. */
  operation: TierOperation;
  /** Wording that asked only for a different presentation. */
  viewOnly: Array<string>;
  /** Set when the request wants a rate a dataset stores as a column. */
  ratioFault?: RatioFault;
};

export type TierProposal = {
  tier: LensTier;
  /** One sentence, for the model to state and for the refusal to quote. */
  reason: string;
  /** Which side of the aggregation a field belongs on. */
  when?: 'row' | 'aggregate';
  evidence: TierEvidence;
};

/** How a Luxury Escapes dataset spells the column a period groups by. */
const DATE_COLUMN = /(^|_)(date|day|week|month|quarter|year|period)($|_)/i;

type Concept = {
  id: string;
  /** How a reader says it. */
  words: RegExp;
  /** How a Luxury Escapes dataset spells it. */
  column: RegExp;
  kind: 'measure' | 'dimension' | 'period';
};

/**
 * The Luxury Escapes vocabulary, as a reader's word beside the column names it
 * can land on. Listed rather than derived: `revenue` and `TTV` are the same
 * column here and different words elsewhere, and no rule over column names
 * alone knows that.
 */
const CONCEPTS: Array<Concept> = [
  {
    id: 'TTV',
    words: /\b(ttv|total transaction value|gross sales|top ?line)\b/i,
    column: /(^|_)(ttv|total_transaction_value)/i,
    kind: 'measure',
  },
  { id: 'revenue', words: /\brevenue\b/i, column: /(revenue|ttv|sales_aud)/i, kind: 'measure' },
  { id: 'margin', words: /\b(margin|gross margin|gm)\b/i, column: /margin/i, kind: 'measure' },
  { id: 'refunds', words: /\brefund(s|ed)?\b/i, column: /refund/i, kind: 'measure' },
  {
    id: 'orders',
    words: /\b(orders?|bookings?|purchases?|transactions?)\b/i,
    column: /(^|_)(orders|bookings|purchases|transactions)($|_)/i,
    kind: 'measure',
  },
  { id: 'spend', words: /\b(spend|cost|budget|cogs)\b/i, column: /(spend|cost)/i, kind: 'measure' },
  { id: 'clicks', words: /\bclicks?\b/i, column: /click/i, kind: 'measure' },
  { id: 'impressions', words: /\bimpressions?\b/i, column: /impression/i, kind: 'measure' },
  { id: 'sessions', words: /\b(sessions?|visits?|traffic|visitors?)\b/i, column: /(session|visit)/i, kind: 'measure' },
  { id: 'room nights', words: /\b(room ?nights?|nights?)\b/i, column: /night/i, kind: 'measure' },
  { id: 'members', words: /\b(members?|subscribers?)\b/i, column: /(member|subscriber)/i, kind: 'measure' },
  { id: 'renewals', words: /\brenewals?\b|\brenewed\b/i, column: /renew/i, kind: 'measure' },
  { id: 'subscription fees', words: /\b(fees?|subscription)\b/i, column: /fee/i, kind: 'measure' },
  { id: 'savings', words: /\bsavings?\b/i, column: /saving/i, kind: 'measure' },
  { id: 'customers', words: /\bcustomers?\b/i, column: /customer/i, kind: 'measure' },
  { id: 'passengers', words: /\b(passengers?|seats?|pax)\b/i, column: /(passenger|seat|pax)/i, kind: 'measure' },
  { id: 'a market', words: /\bmarkets?\b/i, column: /market/i, kind: 'dimension' },
  { id: 'a country', words: /\bcountr(y|ies)\b/i, column: /country/i, kind: 'dimension' },
  { id: 'a region', words: /\bregions?\b/i, column: /region/i, kind: 'dimension' },
  { id: 'a product', words: /\bproducts?\b/i, column: /product/i, kind: 'dimension' },
  { id: 'a brand', words: /\bbrands?\b/i, column: /brand/i, kind: 'dimension' },
  { id: 'a channel', words: /\bchannels?\b/i, column: /channel/i, kind: 'dimension' },
  { id: 'a supplier', words: /\b(suppliers?|vendors?)\b/i, column: /(supplier|vendor)/i, kind: 'dimension' },
  { id: 'a tier', words: /\btiers?\b/i, column: /tier/i, kind: 'dimension' },
  {
    id: 'a hotel',
    words: /\b(hotels?|propert(y|ies)|offers?)\b/i,
    column: /(hotel|property|offer)/i,
    kind: 'dimension',
  },
  { id: 'a destination', words: /\bdestinations?\b/i, column: /destination/i, kind: 'dimension' },
  {
    id: 'a date',
    words: /\b(daily|day|weekly|week|monthly|month|quarterly|quarter|year|date|over time|trend)\b/i,
    column: DATE_COLUMN,
    kind: 'period',
  },
];

/**
 * A business line named as a value rather than a column: "GHA margin" asks for
 * one value of a campaign or product column, not for a column called GHA. A
 * board carrying none of the columns such a value could live in cannot answer
 * it at all, which is what makes these worth detecting separately.
 */
const SEGMENTS: Array<{ id: string; words: RegExp; column: RegExp }> = [
  { id: 'GHA', words: /\bgha\b|\bgoogle hotel ads?\b/i, column: /(gha|campaign|channel|source)/i },
  { id: 'Bedbank', words: /\b(bedbank|lpp|ltlp)\b/i, column: /(product|supplier|vendor|channel|brand)/i },
  { id: 'LuxPlus', words: /\b(lux ?plus|luxplus\+?)\b/i, column: /(product|member|tier|segment|subscription)/i },
  { id: 'flights', words: /\bflights?\b/i, column: /(product|flight|type)/i },
  { id: 'tours', words: /\btours?\b/i, column: /(product|type)/i },
  { id: 'experiences', words: /\bexperiences?\b/i, column: /(product|type)/i },
  { id: 'villas', words: /\b(villas?|rentals?)\b/i, column: /(product|type)/i },
  { id: 'cruise', words: /\bcruises?\b/i, column: /(product|type)/i },
];

const OPERATIONS: Array<{ operation: TierOperation; words: RegExp }> = [
  {
    operation: 'perUnit',
    words:
      /\b(aov|arpu|arpo|average order value)\b|\bper (order|booking|customer|member|night|room|guest|session|click|visit|passenger)\b/i,
  },
  {
    operation: 'share',
    words:
      /\bshare\b|\bmix\b|\bcontribution\b|\bpenetration\b|\battach(ment)? rate\b|\b(as a )?(percentage|proportion|percent|%) of\b|\bof (the )?total\b/i,
  },
  {
    operation: 'ratio',
    words:
      /\brates?\b|\bratio\b|\bpercent(age)?\b|\bpct\b|%|\bconversion\b|\bctr\b|\bm?roas\b|\byield\b|\bmargin ?%|\btake rate\b/i,
  },
  { operation: 'average', words: /\baverage\b|\bavg\b|\bmean\b|\btypical\b/i },
  {
    operation: 'difference',
    words: /\b(difference|gap|delta|uplift|variance|net of|versus|vs\.?)\b|\bless (refunds|cost|spend)\b/i,
  },
  {
    operation: 'conditionalSubtotal',
    words:
      /\bsubtotal\b|\bits own (column|series|measure|line)\b|\bside by side with (the )?total\b|\bbeside (the )?total\b/i,
  },
];

const VIEW_WORDS: Array<{ id: string; words: RegExp }> = [
  {
    id: 'a different chart type',
    words:
      /\b(bar|line|pie|stacked|area|scatter|heat ?map|box ?plot|table|column|donut) chart\b|\bas a (bar|line|pie|table|heat ?map|scatter|stacked|area)\b|\bmake (it|this) a\b|\bswitch (it |this )?to a\b|\bshow (it|this) as\b|\bturn (it|this) into a\b/i,
  },
  {
    id: 'a sort order',
    words:
      /\bsort\b|\border (it |them )?by\b|\brank(ed)?\b|\bdescending\b|\bascending\b|\bhighest first\b|\blowest first\b|\bbiggest first\b/i,
  },
  { id: 'a row limit', words: /\btop \d+\b|\bbottom \d+\b|\bfirst \d+\b|\blimit\b|\bonly (the )?top\b/i },
  {
    id: 'an axis or format change',
    words:
      /\baxis\b|\bsecond scale\b|\bgrid ?lines?\b|\blegend\b|\bdata labels?\b|\bcolours?\b|\bcolors?\b|\bdate format\b|\bdecimal (place|point)s?\b|\bcurrency symbol\b|\bre-?format\b|\bformat (it|this)\b/i,
  },
  {
    id: 'a different split',
    words:
      /\bsplit (it |this )?by\b|\bbreak (it |this )?down by\b|\bgroup (it |this )?by\b|\bstack (it |this )?by\b|\bby (week|month|quarter|day)\b/i,
  },
];

function matchColumns(dataset: TierDataset, matcher: RegExp): Array<string> {
  return dataset.columns.filter((column) => matcher.test(column));
}

/** Everything a request names, and whether this dataset carries it. */
function resolveAgainst(ask: string, dataset: TierDataset) {
  const wanted = CONCEPTS.filter((concept) => concept.words.test(ask));
  const matched: Array<string> = [];
  const measures: Array<string> = [];
  const missing: Array<string> = [];

  for (const concept of wanted) {
    const columns = matchColumns(dataset, concept.column);
    if (columns.length) {
      for (const column of columns) {
        if (!matched.includes(column)) {
          matched.push(column);
        }
        // A declared kind, when this column has one, overrides the concept's own guess: a
        // business word like "orders" is a measure in general, but a column it happens to match
        // can be an id (declared 'dimension') or a hand-classified measure the word alone would
        // have called a label (declared 'measure'). No declaration for this column falls back to
        // what the matched concept implies, exactly as before this existed.
        const declaredKind = dataset.kinds?.[column];
        const isMeasure = declaredKind ? declaredKind === 'measure' : concept.kind === 'measure';
        if (isMeasure && !measures.includes(column)) {
          measures.push(column);
        }
      }
      continue;
    }
    // A period is not missing data when the dataset is dated: a daily dataset
    // answers a monthly card by grouping, which is a view change.
    if (concept.kind === 'period' && dataset.columns.some((column) => DATE_COLUMN.test(column))) {
      continue;
    }
    missing.push(concept.id);
  }

  for (const segment of SEGMENTS.filter((entry) => entry.words.test(ask))) {
    if (!matchColumns(dataset, segment.column).length) {
      missing.push(segment.id);
    }
  }

  return { matched, measures, missing, wanted };
}

function detectOperation(ask: string): TierOperation {
  return OPERATIONS.find((entry) => entry.words.test(ask))?.operation ?? 'none';
}

function detectViewWords(ask: string): Array<string> {
  return VIEW_WORDS.filter((entry) => entry.words.test(ask)).map((entry) => entry.id);
}

/** A denominator an average or per-unit measure can be taken over. */
const DENOMINATORS =
  /(^|_)(orders|bookings|customers|members|sessions|visits|nights|impressions|clicks|passengers|transactions)($|_)/i;

/**
 * Whether this dataset carries both halves of the arithmetic the request asks
 * for. A rate over one column and nothing to divide it by is not a field: it is
 * a query for the column that is missing.
 */
function hasBothHalves(
  ask: string,
  operation: TierOperation,
  dataset: TierDataset,
  measures: ReadonlyArray<string>,
): { ok: boolean; parts?: { of: string; per: string } } {
  if (operation === 'difference' || operation === 'conditionalSubtotal') {
    return { ok: measures.length >= 1 };
  }

  // "TTV per room night" names its own denominator, which is the one signal
  // that beats any convention about what a rate is usually divided by.
  const named = perUnitDenominator(ask, dataset);
  if (named) {
    const of = measures.find((column) => column !== named && !isPerUnitColumn(column));
    if (of) {
      return { ok: true, parts: { of, per: named } };
    }
  }

  const byAsk = ratioPartsForAsk(ask, dataset.columns, dataset.kinds);
  if (byAsk) {
    return { ok: true, parts: byAsk };
  }

  const numerators = measures.filter((column) => !isPerUnitColumn(column));
  if (operation === 'average' || operation === 'perUnit') {
    const of = numerators[0];
    const per = of ? dataset.columns.find((column) => column !== of && DENOMINATORS.test(column)) : undefined;
    return of && per ? { ok: true, parts: { of, per } } : { ok: false };
  }

  if (numerators.length >= 2) {
    return { ok: true, parts: { of: numerators[0], per: numerators[1] } };
  }
  const single = measures[0];
  const parts = single
    ? ratioParts(isPerUnitColumn(single) ? single : `${single}_rate`, dataset.columns, dataset.kinds)
    : undefined;
  return parts ? { ok: true, parts } : { ok: false };
}

/** The column a "per X" phrase names as the denominator, when a dataset has it. */
function perUnitDenominator(ask: string, dataset: TierDataset): string | undefined {
  const tail = /\bper\s+([a-z][a-z\s_]{0,24})/i.exec(ask)?.[1];
  if (!tail) {
    return undefined;
  }
  const concept = CONCEPTS.find((entry) => entry.kind === 'measure' && entry.words.test(tail));
  return concept ? dataset.columns.find((column) => concept.column.test(column)) : undefined;
}

/** `dataset.table` references a request names, lower-cased, without any project prefix. */
function tablesNamedIn(ask: string): Array<string> {
  const references = ask.toLowerCase().matchAll(/\b(?:[a-z][\w-]*\.)?([a-z]\w{2,}\.[a-z]\w{2,})\b/g);
  return [...new Set([...references].map((reference) => reference[1]))];
}

function best(a: TierEvidence, b: TierEvidence): TierEvidence {
  if (a.missing.length !== b.missing.length) {
    return a.missing.length < b.missing.length ? a : b;
  }
  return a.matched.length >= b.matched.length ? a : b;
}

/**
 * The cheapest tier that can plausibly answer this request, and the evidence
 * for it.
 *
 * Deterministic and column-driven, so it can prime the prompt with what already
 * exists and then hold the model's own choice to the same standard. It reads
 * the wording and the dataset columns only: it does not know what the numbers
 * mean, so it is deliberately conservative about a query, which is the one
 * answer that is never wrong, only wasteful.
 */
export function proposeTier(ask: string, datasets: ReadonlyArray<TierDataset>): TierProposal {
  const text = ask.trim();
  const operation = detectOperation(text);
  const viewOnly = detectViewWords(text);

  if (!text) {
    return {
      tier: 'query',
      reason: 'Nothing was asked, so nothing can be answered from what the board already has.',
      evidence: { matched: [], measures: [], missing: [], operation: 'none', viewOnly },
    };
  }

  if (!datasets.length) {
    return {
      tier: 'query',
      reason: 'This board has no shared dataset yet, so the data has to be read from the warehouse.',
      evidence: { matched: [], measures: [], missing: [], operation, viewOnly },
    };
  }

  const unread = tablesNamedIn(text).filter(
    (table) => !datasets.some((dataset) => dataset.reads?.toLowerCase().includes(table)),
  );
  if (unread.length) {
    return {
      tier: 'query',
      reason: `This asks for ${unread.join(', ')}, which no dataset on this board reads.`,
      evidence: { matched: [], measures: [], missing: [], operation, viewOnly },
    };
  }

  let evidence: TierEvidence | undefined;
  let parts: { of: string; per: string } | undefined;
  let halves = false;

  for (const dataset of datasets) {
    const resolved = resolveAgainst(text, dataset);
    const candidate: TierEvidence = {
      datasetId: dataset.id,
      datasetName: dataset.name,
      matched: resolved.matched,
      measures: resolved.measures,
      missing: resolved.missing,
      operation,
      viewOnly,
    };
    const previous = evidence;
    evidence = previous ? best(previous, candidate) : candidate;
    if (evidence === candidate) {
      const both = hasBothHalves(text, operation, dataset, resolved.measures);
      halves = both.ok;
      parts = both.parts;
    }
  }

  const found = evidence;
  const storedRate = found.matched.find((column) => isPerUnitColumn(column));
  if (
    storedRate &&
    (operation === 'ratio' || operation === 'share' || operation === 'average' || operation === 'perUnit')
  ) {
    found.ratioFault = {
      column: storedRate,
      message: `${storedRate} is stored as a rate, so it cannot be rolled up. Compute the rate from its two totals as an aggregate field instead.`,
      ...(parts ? { parts } : {}),
    };
  }

  if (found.missing.length) {
    return {
      tier: 'query',
      reason: `No dataset on this board carries ${found.missing.join(' or ')}, so this needs a warehouse query.`,
      evidence: found,
    };
  }

  if (operation !== 'none') {
    if (!halves) {
      return {
        tier: 'query',
        reason: `This asks for ${describeOperation(operation)} and the board carries only ${found.matched.join(', ') || 'nothing it names'}, so the other half has to be read from the warehouse.`,
        evidence: found,
      };
    }
    const when = operation === 'conditionalSubtotal' ? 'row' : 'aggregate';
    const how = parts ? `${parts.of} over ${parts.per}` : found.matched.join(' and ');
    return {
      tier: 'field',
      when,
      reason: `${found.datasetId} already returns ${how}, so this is ${describeOperation(operation)} as a calculated field evaluated ${when === 'row' ? 'on each row' : 'on totals'}.`,
      evidence: found,
    };
  }

  if (found.matched.length || viewOnly.length) {
    const named = viewOnly.length ? viewOnly.join(' and ') : 'the columns it names';
    return {
      tier: 'view',
      reason: found.matched.length
        ? `${found.datasetId} already returns ${found.matched.join(', ')} and nothing has to be recomputed, so this is ${viewOnly.length ? named : 'a view change'}.`
        : `This asks for ${named} only, so nothing has to be recomputed.`,
      evidence: found,
    };
  }

  return {
    tier: 'query',
    reason: 'Nothing this asks for matches a column the board already has, so it needs a warehouse query.',
    evidence: found,
  };
}

function describeOperation(operation: TierOperation): string {
  switch (operation) {
    case 'ratio':
      return 'a rate';
    case 'share':
      return 'a share of a total';
    case 'average':
      return 'an average';
    case 'perUnit':
      return 'a per-unit value';
    case 'difference':
      return 'a difference between two measures';
    case 'conditionalSubtotal':
      return 'a subtotal under a condition';
    default:
      return 'arithmetic over existing columns';
  }
}

const TIER_COMMENT = /^\s*--\s*tier:\s*(query|field|view)\s*$/im;
const TIER_REASON_COMMENT = /^\s*--\s*tier ?reason:\s*(.+?)\s*$/im;

export type TierDeclaration = { tier?: LensTier; reason?: string };

/** Reads the tier the model stated, from the two lines the prompt asks for. */
export function extractTierDeclaration(answer: string): TierDeclaration {
  const tier = answer.match(TIER_COMMENT)?.[1]?.toLowerCase() as LensTier | undefined;
  const reason = answer.match(TIER_REASON_COMMENT)?.[1]?.trim();
  return { ...(tier ? { tier } : {}), ...(reason ? { reason } : {}) };
}

export type TierRefusal = {
  kind: 'tier';
  /** What the model reached for. */
  requested: LensTier;
  /** The cheapest tier that answers it. */
  allowed: LensTier;
  /** Reader-facing, naming the alternative so the UI can offer it. */
  message: string;
  alternative: {
    tier: LensTier;
    datasetId?: string;
    columns: Array<string>;
    when?: 'row' | 'aggregate';
  };
};

/**
 * Holds a model's own choice of tier to the deterministic one.
 *
 * Only the expensive direction is refused. A query where a field or a view
 * change would do puts bytes, a wait and a second version of the same number on
 * the board, and none of that is visible in the result. The other direction
 * already fails closed: a field or a view naming a column the dataset does not
 * return is rejected where it is parsed and falls through to a query.
 *
 * A proposal that tried the cheaper rung and could not make it work is allowed
 * through: `attempted` says the model bound a dataset, declared a field or
 * rewrote a dataset's query, and the parse of that binding is what rejected it.
 */
export function checkTierProposal(input: {
  declared: TierDeclaration;
  proposal: TierProposal;
  /** True when the answer tried a dataset, field or extension binding. */
  attempted: boolean;
}): TierRefusal | undefined {
  const { declared, proposal, attempted } = input;
  const requested = declared.tier ?? 'query';
  if (attempted || TIER_RANK[requested] >= TIER_RANK[proposal.tier] || proposal.tier === 'query') {
    return undefined;
  }

  const columns = proposal.evidence.matched;
  const named = proposal.evidence.datasetName ?? proposal.evidence.datasetId;
  const where = named ? `the ${named} dataset` : 'a dataset on this board';
  const message =
    proposal.tier === 'field'
      ? `Nothing was added. This asked for a new warehouse query, but ${where} already returns ${columns.join(', ')}, so the number is a calculated field over those columns evaluated ${proposal.when === 'row' ? 'on each row' : 'on totals'}. Ask for it as a field instead.`
      : `Nothing was added. This asked for a new warehouse query, but ${where} already returns ${columns.join(', ')} and nothing has to be recomputed, so this is a change to how an existing card is shown.`;

  return {
    kind: 'tier',
    requested,
    allowed: proposal.tier,
    message,
    alternative: {
      tier: proposal.tier,
      ...(proposal.evidence.datasetId ? { datasetId: proposal.evidence.datasetId } : {}),
      columns,
      ...(proposal.when ? { when: proposal.when } : {}),
    },
  };
}
